import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { Image } from "../ui/Image";
import LazyImage from "../ui/LazyImage";
import { CountryList } from "@/types/global";
import { useCountries } from "@/store/hooks/useCountries";
import useClickOutside from "@/hooks/useClickOutSide";
import { ShimmerLoader } from "../shimmer/Shimmer";

interface Props {
  defaultValue?: CountryList;
  setSelected: (option: CountryList) => void;
  onChangeCallback: () => void;
  select: CountryList;
  className?: string;
  selectedText: string;
  disabled: boolean;
}

const CountrySelectBox: React.FC<Props> = ({ defaultValue, setSelected, onChangeCallback, select, className = "", selectedText, disabled }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [query, setQuery] = useState("");
  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    if (query) setQuery("");
  }, [query]);
  const containerRef = useClickOutside(handleCloseDropdown);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const { data: options, userLocation, refetchCountry, isLoading } = useCountries({ autoFetch: true });

  useEffect(() => {
    if (defaultValue && userLocation && options) {
      if (userLocation?.data?.country === "IN" || defaultValue?.cid === 99) {
        const country = options.filter((c) => c.cid == defaultValue.cid);
        if (country?.length > 0) {
          setSelected(country[0]);
        }
      } else {
        const country = options.filter((c) => c.iso_name == userLocation?.data?.country);
        if (country?.length > 0) {
          setSelected(country[0]);
        }
      }
    }
  }, [defaultValue, options, userLocation]);

  useEffect(() => {
    if (isDropdownOpen && options?.length === 0) {
      refetchCountry();
    }
  }, [isDropdownOpen]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    if (!isDropdownOpen) setIsDropdownOpen(true);

    const queryLower = query.toLowerCase();

    const categorizeOption = (option: any) => {
      const isoName = option.iso_name.toLowerCase();
      const phoneCode = option.country_phone_code;
      const name = option.name.toLowerCase();

      if (isoName === queryLower) return "exactCode";
      if (phoneCode === queryLower) return "exactPhone";
      if (name === queryLower) return "exactName";
      if (isoName.includes(queryLower)) return "partialCode";
      if (phoneCode.includes(queryLower)) return "partialPhone";
      if (name.includes(queryLower)) return "partialName";
      return null;
    };

    const categorizedResults: any = {
      exactCode: [],
      exactPhone: [],
      exactName: [],
      partialCode: [],
      partialPhone: [],
      partialName: [],
    };

    for (const option of options) {
      const category = categorizeOption(option);
      if (category) {
        categorizedResults[category].push(option);
      }
    }

    return [
      ...categorizedResults.exactCode,
      ...categorizedResults.exactPhone,
      ...categorizedResults.exactName,
      ...categorizedResults.partialCode,
      ...categorizedResults.partialPhone,
      ...categorizedResults.partialName,
    ];
  }, [isDropdownOpen, options, query]);

  const handleSelect = useCallback(
    (option: CountryList) => {
      setQuery("");
      setSelected(option);
      setIsDropdownOpen(false);
      if (option?.cid !== select?.cid) {
        onChangeCallback();
      }
    },
    [setSelected, select]
  );

  useEffect(() => {
    if (inputRef.current && measureRef.current) {
      if (isDropdownOpen) {
        inputRef.current.style.width = "100%";
        inputRef.current.focus();
      } else {
        // actual width of the input text content
        const textWidth = measureRef.current.offsetWidth;
        // console.log({ textWidth, M: measureRef.current });
        inputRef.current.style.width = `${textWidth - 6}px`;
      }
    }
  }, [selectedText, isDropdownOpen, isLoading]);

  const renderOption = useCallback(
    (option: CountryList, isSelected: boolean = false) => (
      <li
        key={option.iso_name}
        className={`hover:bg-brand-light ${
          isSelected ? "bg-brand-light" : ""
        } hover:text-primary text-text-default text-start hover:cursor-pointer p-1.5 text-13 grid grid-cols-[24px,1fr] gap-2 items-center`}
        onClick={() => handleSelect(option)}
      >
        <LazyImage
          src={`https://lp-asset.gigglly.com/asset/flags/${option.iso_name}.svg`}
          alt={`${option.name} flag`}
          className="max-w-full max-h-full object-contain border"
        />
        <div className="overflow-hidden line-clamp-2">
          <span className="font-bold whitespace-nowrap">{`${option.iso_name} (${option.country_phone_code})`}</span>{" "}
          <span className="capitalize ">{option.nicename.toLowerCase()}</span>
        </div>
      </li>
    ),
    [handleSelect]
  );
  if (isLoading) return <ShimmerLoader className="w-24 h-5" />;

  return (
    <div className={`select-none transition-all duration-100 ease-in-out ${isDropdownOpen ? "w-[220px]" : ""} ${className}`} ref={containerRef}>
      <div className="flex gap-1.5 items-center border-r-2 pl-2" onClick={() => !disabled && setIsDropdownOpen((prev) => !prev)}>
        {!isDropdownOpen && <Image isExternal src={`https://lp-asset.gigglly.com/asset/flags/${select?.iso_name}.svg`} alt="flag-icon" className="h-5 w-6" />}{" "}
        <div className=" flex items-center w-full">
          <input
            type="text"
            ref={inputRef}
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            disabled={disabled}
            placeholder={isDropdownOpen ? "Search..." : selectedText}
            className={`w-full text-nowrap disabled:cursor-not-allowed text-13 outline-none placeholder:text-text-default`}
          />
          <ChevronDown className={`cursor-pointer text-gray-500 bg-transparent ${isDropdownOpen ? "rotate-180" : ""}`} height={20} width={20} />
        </div>
      </div>
      <span
        ref={measureRef}
        className="absolute left-0 top-0 invisible whitespace-pre text-13"
        style={{
          font: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
          paddingLeft: "inherit",
          paddingRight: "inherit",
        }}
      >
        {selectedText}
      </span>

      {isDropdownOpen && (
        <div className="w-full mt-2.5 flex items-center border border-t-0 max-h-[200px] max-w-[220px] flex-col absolute bg-white z-50 shadow-2xl">
          <ul className="flex flex-col justify-start w-full py-1 pb-2 overflow-auto h-fit tablet:py-2 tablet:pb-3">
            {select && renderOption(select, true)}
            {select && <li className="border-b border-gray-200 my-1"></li>}
            {filteredOptions.length > 0 ? (
              filteredOptions.filter((option) => option.iso_name !== select?.iso_name).map((option) => renderOption(option))
            ) : query ? (
              <p className="text-text-error text-center text-xs py-1">Result Not Found!</p>
            ) : options.length === 0 ? (
              <span className="text-13 text-center">Loading</span>
            ) : (
              options.filter((option) => option.iso_name !== select?.iso_name).map((option) => renderOption(option))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default React.memo(CountrySelectBox);
